import React, { useState, useEffect, useMemo } from "react";
import Page from "../shared/components/PageComponent";
import Layout from "../shared/components/LayoutComponent";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { SearchTextField } from "../lib";
import { useTranslation } from "react-i18next";
import apiRequest from "../../functions/api";
import { useMessage } from "../../context/message-context";
import ConectionCard from "./ConectionCard";
import KabelEditDialog from "./KabelEditDialog";

// Icons
import { FilterList } from "@mui/icons-material";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";

const program = "KMPAPI";

function DeviceDetails(props) {
  const { accessToken, client } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showErrorMessage } = useMessage();
  let { id } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [kabelId, setKabelId] = useState("");

  const [filterDialog, setFilterDialog] = useState(false);
  const [suche, setSuche] = useState("");
  const [kabel, setKabel] = useState([]);
  const [device, setDevice] = useState("");
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [deviceFilter, setDeviceFilter] = useState([]);
  const [einheitFilter, setEinheitFilter] = useState([]);
  const [idFilter, setIdFilter] = useState([]);
  const [editDisabled, setEditDisabled] = useState(false);
  const [dialogVersion, setDialogVersion] = useState(0);
  const [optionen, setOptionen] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const einheitOptions = useMemo(() => {
    // Kombinieren von startGeraet und zielGeraet
    const allEinheiten = kabel
      ?.map((mapElement) => [mapElement.startEinheit, mapElement.zielEinheit])
      .flat()
      ?.filter((item) => item !== null && item !== undefined);

    // Entfernen von Duplikaten
    const uniqueEinheiten = [...new Set(allEinheiten)];

    // Erstellen der Optionen
    return uniqueEinheiten.map((einheit) => {
      return {
        value: einheit,
        label: einheit,
      };
    });
  }, [kabel]);

  const deviceOptions = useMemo(() => {
    // Kombinieren von startGeraet und zielGeraet
    const allGeraete = kabel
      ?.map((mapElement) => [mapElement.startGeraet, mapElement.zielGeraet])
      .flat()
      ?.filter((item) => item !== null && item !== undefined);

    // Entfernen von Duplikaten
    const uniqueGeraete = [...new Set(allGeraete)];

    // Erstellen der Optionen
    return uniqueGeraete.map((geraet) => {
      return {
        value: geraet,
        label: geraet,
      };
    });
  }, [kabel]);

  const idOptions = useMemo(() => {
    // Herrausfiltern der einzelnen Optionen
    const tmpfilter = kabel
      ?.filter((value, index, array) => array.findIndex((element) => element.ID === value.ID) === index)
      .map((mapElement) => {
        return {
          value: mapElement.ID,
          label: mapElement.ID,
        };
      })
      ?.filter((item) => item !== null);
    return tmpfilter ? tmpfilter : [];
  }, [kabel]);

  // ----------------------------------------------------
  // -----------Allgemeine funktionen--------------------
  // ----------------------------------------------------
  useEffect(() => {
    setIsLoading(true);
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      id: id,
      statement: "DEVICE_DETAILS",
    };

    apiRequest("POST", "daten", payload, headers, datenCb, errorCb);
  }, [id]);

  useEffect(() => {
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "KABEL_OPTIONEN",
    };

    apiRequest("POST", "daten", payload, headers, optionCb, errorCb);
  }, []);

  useEffect(() => {
    const result = kabel?.filter((item) => {
      const tmpDevices = deviceFilter?.length !== 0 ? deviceFilter?.some((searchItem) => [item.startGeraet, item.zielGeraet].includes(searchItem.label)) : true;
      const tmpEinheit =
        einheitFilter?.length !== 0 ? einheitFilter?.some((searchItem) => [item.startEinheit, item.zielEinheit].includes(searchItem.label)) : true;
      const tmpId = idFilter?.length !== 0 ? idFilter?.some((searchItem) => searchItem.label === item.ID) : true;

      const tmpSuche =
        suche?.trim() !== ""
          ? [item.startGeraet, item.zielGeraet, item.startEinheit, item.zielEinheit, item.ID].some((field) =>
              field?.toLowerCase().includes(suche.toLowerCase()),
            )
          : true;
      return tmpSuche && tmpDevices && tmpEinheit && tmpId;
    });

    setFilteredDetails(result);
  }, [kabel, suche, deviceFilter, einheitFilter, idFilter]);

  const datenCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setDevice(response.data.BEZ);
      setKabel(response.data.kabel);
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const optionCb = (response) => {
    if (response.status === 200) {
      setOptionen(response.data);
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const errorCb = () => {
    showErrorMessage(t("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."));
  };

  const resetFilter = () => {
    setSuche("");
    setDeviceFilter([]);
    setEinheitFilter([]);
    setIdFilter([]);
  };

  // ----------------------------------------------------
  // -------------Handy funktionen-----------------------
  // ----------------------------------------------------
  const MobileMain = () => {
    return (
      <>
        <Box sx={{ paddingTop: 2 }}>
          <Box>
            {isLoading && (
              <Box display="flex" justifyContent="end" alignItems="center" paddingTop={2}>
                <CircularProgress size={24}></CircularProgress>
              </Box>
            )}

            {!isLoading && (
              <>
                <Typography>{t("Aktives Gerät:")}</Typography>
                <Typography sx={{ fontWeight: "bold" }}>{device}</Typography>
              </>
            )}
            <Divider component="li" />
            <TableContainer sx={{ maxHeight: window.innerHeight - 250 }}>
              {!isLoading &&
                filteredDetails?.map((item) => {
                  return (
                    <ConectionCard
                      key={item.ID}
                      kabel={item}
                      setKabelId={setKabelId}
                      setDialogVersion={setDialogVersion}
                      setEditDisabled={setEditDisabled}
                      setOpenDialog={setOpenDialog}
                    />
                  );
                })}
            </TableContainer>
          </Box>
        </Box>
      </>
    );
  };

  // ----------------------------------------------------
  // -------------Desktop funktionen---------------------
  // ----------------------------------------------------
  const DesktopMain = () => {
    return <>Desktop nicht fertig</>;
  };

  return (
    <Page>
      <Layout>
        {/* Desktop */}
        <Box sx={{ display: { xs: "none", sm: "none", md: "block" }, width: "100%" }}>
          {/* Filter */}
          <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
            <Grid item xs={12} md={1}>
              <Tooltip title={t("Zurück zur Hauptauswahl")}>
                <IconButton color="primary" onClick={() => navigate("/KMPAPI/device")}>
                  <ReplyAllIcon />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={3}>
              <SearchTextField
                size="small"
                fullWidth
                label={t("Suche")}
                id="filter"
                variant="outlined"
                value={suche}
                inputProps={{
                  autoCapitalize: "off",
                  autoCorrect: "off",
                  spellCheck: "false",
                  autoComplete: "off",
                }}
                onChange={(e) => setSuche(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setDeviceFilter(newValue);
                }}
                value={deviceFilter}
                size="small"
                id="tags-outlined"
                options={deviceOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Gerät filtern" placeholder="Gerät Filter" />}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setEinheitFilter(newValue);
                }}
                value={einheitFilter}
                size="small"
                id="tags-outlined"
                options={einheitOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Einheit (Rack) filtern" placeholder="Einheit Filter" />}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setIdFilter(newValue);
                }}
                value={idFilter}
                size="small"
                id="tags-outlined"
                options={idOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Kabel ID filtern" placeholder="ID Filter" />}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <IconButton color="error" onClick={() => resetFilter()}>
                <FilterListOffIcon />
              </IconButton>
            </Grid>
          </Grid>

          <MobileMain />
        </Box>

        {/* Mobile */}
        <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
          {/* Filter */}
          <Box sx={{ paddingTop: 2 }} display="flex" alignItems={"center"}>
            <Tooltip title={t("Zurück zu den Geräten")}>
              <IconButton color="primary" onClick={() => navigate("/KMPAPI/device/")}>
                <ReplyAllIcon />
              </IconButton>
            </Tooltip>
            <SearchTextField
              size="small"
              fullWidth
              label={t("Suche")}
              id="filter"
              variant="outlined"
              sx={{ marginLeft: "16px" }}
              value={suche}
              inputProps={{
                autoCapitalize: "off",
                autoCorrect: "off",
                spellCheck: "false",
                autoComplete: "off",
              }}
              onChange={(e) => setSuche(e.target.value)}
            />

            <Box pr={1}>
              <Tooltip title="Filter">
                <IconButton color="primary" onClick={() => setFilterDialog(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Box>
            <Box pr={1}>
              <Tooltip title="Filter entfernen">
                <IconButton color="error" onClick={() => resetFilter()}>
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="md" fullWidth>
              <DialogTitle>Filter</DialogTitle>
              <DialogContent>
                <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
                  <Grid item xs={12} md={5}></Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setDeviceFilter(newValue);
                      }}
                      value={deviceFilter}
                      size="small"
                      id="tags-outlined"
                      options={deviceOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Gerät filtern" placeholder="Gerät Filter" />}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setEinheitFilter(newValue);
                      }}
                      value={einheitFilter}
                      size="small"
                      id="tags-outlined"
                      options={einheitOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Einheit (Rack) filtern" placeholder="Einheit Filter" />}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setIdFilter(newValue);
                      }}
                      value={idFilter}
                      size="small"
                      id="tags-outlined"
                      options={idOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Kabel ID filtern" placeholder="ID Filter" />}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button style={{ marginRight: "10px" }} color="primary" onClick={() => setFilterDialog(false)}>
                  {t("Fertig")}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          <MobileMain />
        </Box>

        <KabelEditDialog
          accessToken={accessToken}
          client={client}
          disabled={editDisabled}
          dialogVersion={dialogVersion}
          optionen={optionen}
          kabelId={kabelId}
          open={openDialog}
          handleClose={() => setOpenDialog(false)}
        />
      </Layout>
    </Page>
  );
}

export default DeviceDetails;
